/**
 * Browser-safe Auth app env.
 * Do not put AUTH_ORIGIN, INTERNAL_API_URL, or secrets here.
 * Dev proxy upstream is configured in vite.config.ts (Node-only).
 *
 * Never derive origins or API upstream from Host / X-Forwarded-* / request URL.
 */

const trimmed = (value: string | undefined) => value?.trim() || "";

const stripTrailingSlash = (value: string) => value.replace(/\/+$/, "");

/** Browser reads VITE_* and the exact NEXT_PUBLIC_* legacy keys explicitly mapped by vite.config.ts; no broad prefix is exposed. */
const browserPublicValues: Record<string, string | undefined> = {
  VITE_AUTH_ORIGIN: import.meta.env.VITE_AUTH_ORIGIN,
  VITE_WEBSITE_URL: import.meta.env.VITE_WEBSITE_URL,
  VITE_APP_ORIGIN: import.meta.env.VITE_APP_ORIGIN,
  VITE_GOOGLE_CLIENT_ID: import.meta.env.VITE_GOOGLE_CLIENT_ID,
  VITE_GA4_ID: import.meta.env.VITE_GA4_ID,
  VITE_GTM_ID: import.meta.env.VITE_GTM_ID,
  NEXT_PUBLIC_AUTH_ORIGIN: import.meta.env.NEXT_PUBLIC_AUTH_ORIGIN,
  NEXT_PUBLIC_WEBSITE_URL: import.meta.env.NEXT_PUBLIC_WEBSITE_URL,
  NEXT_PUBLIC_APP_ORIGIN: import.meta.env.NEXT_PUBLIC_APP_ORIGIN,
  NEXT_PUBLIC_GOOGLE_CLIENT_ID: import.meta.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID,
  NEXT_PUBLIC_GA4_ID: import.meta.env.NEXT_PUBLIC_GA4_ID,
  NEXT_PUBLIC_GTM_ID: import.meta.env.NEXT_PUBLIC_GTM_ID,
};

const publicEnv = (...keys: string[]): string => {
  for (const key of keys) {
    const fromMeta = browserPublicValues[key];
    if (fromMeta != null && String(fromMeta).trim()) return trimmed(String(fromMeta));
    if (typeof process !== "undefined" && process.env?.[key]) {
      return trimmed(process.env[key]);
    }
  }
  return "";
};

/**
 * Browser credential API base: same-origin `/api` → Vite proxy rewrites to
 * Worker `/v3` (allowlisted credential paths).
 */
export const AUTH_SAME_ORIGIN_API_BASE = "/api";

/**
 * Optional public Auth origin for UI that must absolute-link to this host.
 * Prefer relative same-origin navigation; only use when an absolute URL is required.
 */
export const NEXT_PUBLIC_AUTH_ORIGIN = stripTrailingSlash(
  publicEnv("VITE_AUTH_ORIGIN", "NEXT_PUBLIC_AUTH_ORIGIN"),
);

export const WEBSITE_URL = publicEnv(
  "VITE_WEBSITE_URL",
  "NEXT_PUBLIC_WEBSITE_URL",
);

/**
 * Product app origin for client redirects after auth verification.
 * Browser-safe public env only (no server APP_ORIGIN).
 */
export const APP_ORIGIN = stripTrailingSlash(
  publicEnv("VITE_APP_ORIGIN", "NEXT_PUBLIC_APP_ORIGIN"),
);

export const GOOGLE_CLIENT_ID = publicEnv(
  "VITE_GOOGLE_CLIENT_ID",
  "NEXT_PUBLIC_GOOGLE_CLIENT_ID",
);

/** GA4 measurement id. Empty outside production so the direct tag is not rendered. */
export const GA4_ID = publicEnv("VITE_GA4_ID", "NEXT_PUBLIC_GA4_ID");

/** GTM container id. Empty outside production so the snippet is not rendered. */
export const GTM_ID = publicEnv("VITE_GTM_ID", "NEXT_PUBLIC_GTM_ID");

export const GOOGLE_ANALYTICS = GA4_ID;
export const GOOGLE_TAG_MANAGER = GTM_ID;

/** Optional Google GIS / @react-oauth/google — false when unset (email/password still works). */
export const isGoogleOAuthConfigured = (): boolean => Boolean(GOOGLE_CLIENT_ID);
